import { useState, useEffect } from "react";
import type { AnsweredQuestion, BBox } from "@/api/q-and-a";
import { getCitationBboxes, type CitationSignerHints } from "@/api/documents";
import { mergeOverlappingHighlights } from "../utils/citationProcessing";
import {
  graphCitationIsGrounded,
  isGraphSourcedAnswer,
} from "../utils/graphCitations";
import { ExtendedHighlight } from "@/api/highlights";
import type { CitationData, HighlightMeta, HighlightMetaIndex } from "../types";

const EMPTY_META_INDEX: HighlightMetaIndex = {
  meta: new Map(),
  byCitation: new Map(),
};

/**
 * Build an area highlight from an array of bounding boxes.
 */
const bboxesToAreaHighlight = (
  bboxes: BBox[],
  citationId: string,
  page: number,
  questionText: string,
): ExtendedHighlight => {
  const minLeft = Math.min(...bboxes.map((b) => b.Left));
  const minTop = Math.min(...bboxes.map((b) => b.Top));
  const maxRight = Math.max(...bboxes.map((b) => b.Left + b.Width));
  const maxBottom = Math.max(...bboxes.map((b) => b.Top + b.Height));

  const boundingRect = {
    x1: minLeft,
    y1: minTop,
    x2: maxRight,
    y2: maxBottom,
    width: 1,
    height: 1,
    pageNumber: page,
  };

  return {
    id: `citation-${citationId}`,
    type: "area",
    position: {
      boundingRect,
      rects: [boundingRect],
      usePdfCoordinates: false,
    },
    comment: { text: questionText, emoji: "🔍" },
  };
};

// A text match must land within this normalized (0..1) page distance of the
// citation's bbox to be trusted over the bbox itself. Repeated occurrences of a
// phrase sit far apart (header vs signature block ≈ 0.5+), so the correct one is
// near the bbox and the wrong one is well beyond this.
const NEAR_BBOX_THRESHOLD = 0.2;

/**
 * Choose which text-search match to render for a citation.
 *
 * A citation's phrase can appear multiple times on a page (repeated company
 * names, dollar amounts, dates); blindly taking the first match highlights the
 * WRONG occurrence. When the backend gave us bboxes for this citation, use them
 * to pick the match closest to the true location, and return null if no match is
 * near the bbox so the caller renders the bbox itself. Without bboxes we can't
 * disambiguate identical repeats, so keep the best-scoring match (searchText has
 * already constrained results to the cited page).
 *
 * Coordinate spaces: text-match boundingRects are viewport pixels with
 * width/height = viewport dims (normalize via x/width); bboxes are
 * Textract-normalized 0..1. Both are on the citation's page, so we compare
 * normalized centroids. (Rotated pages use usePdfCoordinates and normalize only
 * approximately — acceptable for a nearest-of-two ranking.)
 */
export const pickBestTextMatch = (
  matches: ExtendedHighlight[],
  bboxes: BBox[] | null | undefined,
): ExtendedHighlight | null => {
  if (matches.length === 0) return null;
  // Single match: no ambiguity — unchanged from the pre-fix behavior.
  if (matches.length === 1) return matches[0];
  // Multiple matches but no bbox anchor: fall back to best fuzzy score (first).
  if (!bboxes || bboxes.length === 0) return matches[0];

  const bboxCx =
    bboxes.reduce((s, b) => s + (b.Left + b.Width / 2), 0) / bboxes.length;
  const bboxCy =
    bboxes.reduce((s, b) => s + (b.Top + b.Height / 2), 0) / bboxes.length;

  let best: ExtendedHighlight | null = null;
  let bestDist = Infinity;
  for (const m of matches) {
    const r = m.position.boundingRect;
    if (!r.width || !r.height) continue;
    const cx = (r.x1 + r.x2) / 2 / r.width;
    const cy = (r.y1 + r.y2) / 2 / r.height;
    const dist = Math.hypot(cx - bboxCx, cy - bboxCy);
    if (dist < bestDist) {
      bestDist = dist;
      best = m;
    }
  }
  // If even the nearest match is far from the bbox, the text search matched
  // unrelated text — prefer the bbox (caller falls through to bbox rendering).
  return bestDist <= NEAR_BBOX_THRESHOLD ? best : null;
};

/**
 * Hook for processing and managing citation highlights in PDF documents.
 *
 * This hook processes Q&A data to extract citations, searches for them in the PDF,
 * and creates visual highlights. It also handles merging overlapping highlights
 * and maintains citation metadata for display.
 *
 * @param documentAnswersData - Array of answered questions containing citations
 * @param pdfDocumentLoaded - Boolean indicating if the PDF document is fully loaded
 * @param searchText - Function to search for text in the PDF document
 *
 * @returns An object containing:
 * - citationHighlights: Array of ExtendedHighlight objects for rendering citations
 * - citationDataMap: Map containing citation metadata (question, answer, explanation)
 * - highlightMetaIndex: Index mapping highlight IDs and citation IDs for O(1) lookups
 */
export const useCitationHighlights = (
  documentAnswersData: AnsweredQuestion[] | undefined,
  pdfDocumentLoaded: boolean,
  searchText: (
    text: string,
    specificPage?: number | null,
  ) => Promise<ExtendedHighlight[]>,
  documentId?: string,
) => {
  const [citationHighlights, setCitationHighlights] = useState<
    Array<ExtendedHighlight>
  >([]);
  const [citationDataMap, setCitationDataMap] = useState<Map<string, CitationData>>(
    new Map(),
  );
  const [highlightMetaIndex, setHighlightMetaIndex] = useState<HighlightMetaIndex>(EMPTY_META_INDEX);
  // True while the sequential per-citation search is still building highlights.
  // On a doc with many citations this takes several seconds; the left pane shows
  // a "Loading highlights…" spinner so the viewer doesn't look inert.
  const [isProcessingHighlights, setIsProcessingHighlights] = useState(false);

  // Process Q&A data and extract citations with proper text search
  useEffect(() => {
    let cancelled = false;

    const processCitations = async () => {
      // Not ready (no answers, or the PDF/doc is (re)loading): nothing is
      // processing, so clear the flag. This also unsticks the spinner when a
      // doc switch cancels a run mid-flight and the next run isn't ready yet.
      if (!documentAnswersData || !pdfDocumentLoaded) {
        setIsProcessingHighlights(false);
        return;
      }
      if (!cancelled) setIsProcessingHighlights(true);
      try {
        // Extract citations from answered questions (updated for answer groups)
        const allCitations = documentAnswersData
          .flatMap((aq) =>
            aq.answer_groups.flatMap((group) =>
              group.answers.flatMap((answer) => {
                // Signature answers carry per-signer metadata. When the
                // signer card has many citations (one signer signing on
                // many pages), each citation needs the matching
                // signature_data row's identity for bbox disambiguation —
                // the page on the citation is the key. Service-block /
                // multi-signer pages share the same page number across
                // signers, so without per-citation hints the matcher
                // falls back to fuzzy text search and lands on the wrong
                // name. Index signature_data by (doc_id, page) so each
                // citation can pick out its own row.
                const sigByDocPage = new Map<string, NonNullable<typeof answer.signature_data>[number]>();
                for (const sig of answer.signature_data ?? []) {
                  const docId = sig.source_document_id ?? "";
                  const page = sig.page ?? 0;
                  sigByDocPage.set(`${docId}|${page}`, sig);
                }
                return answer.citations
                  .filter((citation) => !documentId || citation.document_id === documentId)
                  .map((citation) => {
                    const sigKey = `${citation.document_id}|${citation.page ?? 0}`;
                    const sig = sigByDocPage.get(sigKey)
                      // Single-signature answers (or single-page docs) —
                      // legacy fallback when the (doc_id, page) lookup
                      // misses but the answer has exactly one signer.
                      ?? (answer.signature_data?.length === 1 ? answer.signature_data[0] : undefined);
                    const signerHints: CitationSignerHints | undefined = sig
                      ? {
                          name: sig.signer_name || sig.raw_signer_name || undefined,
                          role: sig.signer_role || undefined,
                          company: sig.signer_company || undefined,
                        }
                      : undefined;
                    return {
                      citationId: citation.id,
                      answerId: answer.id,
                      questionId: aq.question_id,
                      answerGroupId: group.id,
                      citation: citation.citation,
                      page: citation.page,
                      bboxes: (!citation.extracted_from_project && citation.bboxes) || null,
                      questionText: aq.question_text,
                      answerText: answer.text,
                      answerExplanation: answer.explanation,
                      isCurrentValue: answer.is_current_value,
                      isComposite: answer.is_composite,
                      documentId: citation.document_id,
                      filename: citation.filename,
                      signerHints,
                      // Graph / graph_full answers carry broad whole-chunk
                      // citation text; never text-search it (see loop below).
                      isGraphSourced: isGraphSourcedAnswer(answer),
                      // A grounded graph citation has a bbox matched to the
                      // answer's supporting line — safe to highlight. Ungrounded
                      // ones fall back to a page-level reference.
                      isGrounded: graphCitationIsGrounded(citation),
                    };
                  });
              })
            )
          );

        // Deduplicate citations by citationId to avoid creating multiple highlights for the same citation
        // When multiple answers reference the same citation, we should only create one highlight
        const citationMap = new Map<string, typeof allCitations[0]>();
        allCitations.forEach(citation => {
          const existingCitation = citationMap.get(citation.citationId);
          if (!existingCitation) {
            // First occurrence of this citation
            citationMap.set(citation.citationId, citation);
          }
        });

        const extractedCitations = Array.from(citationMap.values());

        const citationHighlights: ExtendedHighlight[] = [];
        const newCitationDataMap = new Map<string, CitationData>();
        const metaMap = new Map<string, HighlightMeta>();
        const byCitationMap = new Map<string, string>();

        const storeCitationData = (citation: (typeof extractedCitations)[0], highlightId: string) => {
          newCitationDataMap.set(highlightId, {
            questionId: citation.questionId,
            answerGroupId: citation.answerGroupId,
            questionText: citation.questionText,
            answerText: citation.answerText,
            answerExplanation: citation.answerExplanation,
            isCurrentValue: citation.isCurrentValue,
            isComposite: citation.isComposite,
            documentId: citation.documentId,
            filename: citation.filename,
          });
          metaMap.set(highlightId, {
            highlightId,
            citationIds: [citation.citationId],
            questionIds: [citation.questionId],
            answerGroupIds: [citation.answerGroupId],
            kind: "citation" as const,
          });
          byCitationMap.set(citation.citationId, highlightId);
        };

        // Build + register an area highlight from the citation's pre-computed
        // bboxes. Callers guarantee page != null and bboxes is non-empty.
        const pushAreaHighlight = (citation: (typeof extractedCitations)[0]) => {
          const areaHighlight = bboxesToAreaHighlight(
            citation.bboxes!,
            citation.citationId,
            citation.page!,
            citation.questionText,
          );
          citationHighlights.push(areaHighlight);
          storeCitationData(citation, areaHighlight.id);
        };

        // First pass: search PDF text layer (sequential — depends on PDF.js state).
        // Signature citations are LLM-generated prose descriptions ("DocuSign
        // signature above printed name 'X' and title 'Y'") rather than verbatim
        // PDF text, and fuse.js fuzzy matching inside searchText routinely
        // lands them on the wrong name/role line when many similar signer
        // blocks share the page. Skip the text layer entirely for those — the
        // backend has already computed signer-disambiguated bboxes.
        const bboxFallbacks: (typeof extractedCitations)[0][] = [];
        for (const citation of extractedCitations) {
          if (!citation.citation) continue; // ignore empty citations
          try {
            // Graph / graph_full citations are broad whole-chunk references, not
            // exact spans, so we never text-search their citation text (it would
            // flood the page). The backend grounds them to their supporting line
            // (`bbox_grounded`): when GROUNDED, render that precise bbox as an
            // area highlight; otherwise (weak/ambiguous match or a "found
            // nothing" answer) render NO highlight — AnswerPane shows a
            // page-level source reference and clicking navigates to the page.
            if (citation.isGraphSourced) {
              if (
                citation.isGrounded &&
                citation.page != null &&
                citation.bboxes &&
                citation.bboxes.length > 0
              ) {
                pushAreaHighlight(citation);
              }
              continue;
            }

            const hasSignerHints = !!citation.signerHints;
            const searchHighlights = hasSignerHints
              ? []
              : await searchText(citation.citation, citation.page);

            // Disambiguate repeated text: don't blindly take the first match —
            // rank by proximity to the citation's bbox. null => no match near
            // the bbox, so fall through to the bbox highlight below.
            const bestMatch =
              searchHighlights.length > 0
                ? pickBestTextMatch(searchHighlights, citation.bboxes)
                : null;

            if (bestMatch) {
              const highlightId = `citation-${citation.citationId}`;
              citationHighlights.push({
                ...bestMatch,
                id: highlightId,
                type: "text" as const,
                comment: { text: citation.questionText, emoji: "🔍" },
              });
              storeCitationData(citation, highlightId);
            } else if (citation.page != null) {
              // Text layer skipped, unmatched, or matches were all far from the
              // citation's bbox (ambiguous repeated text) — use the
              // backend-disambiguated bboxes.
              if (citation.bboxes && citation.bboxes.length > 0) {
                pushAreaHighlight(citation);
              } else if (citation.documentId) {
                // No pre-computed bboxes — fall back to API
                bboxFallbacks.push(citation);
              }
            }
          } catch (error) {
            console.error(
              "Error processing citation:",
              citation.citation,
              error,
            );
          }
        }

        // Second pass: fetch Textract bboxes via API for citations without pre-computed data
        if (bboxFallbacks.length > 0) {
          const results = await Promise.allSettled(
            bboxFallbacks.map((citation) =>
              getCitationBboxes(
                citation.documentId!,
                citation.page!,
                citation.citation!,
                citation.signerHints,
              ).then((result) => ({ citation, ...result })),
            ),
          );

          for (const result of results) {
            if (result.status !== "fulfilled") continue;
            const { citation, bboxes, matched } = result.value;
            if (!matched || bboxes.length === 0) continue;

            const areaHighlight = bboxesToAreaHighlight(
              bboxes,
              citation.citationId,
              citation.page!,
              citation.questionText,
            );
            citationHighlights.push(areaHighlight);
            storeCitationData(citation, areaHighlight.id);
          }
        }

        const initialMetaIndex: HighlightMetaIndex = {
          meta: metaMap,
          byCitation: byCitationMap,
        };

        if (cancelled) return;

        // Merge overlapping highlights
        const { mergedHighlights, updatedCitationDataMap, updatedMetaIndex } =
          mergeOverlappingHighlights(citationHighlights, newCitationDataMap, initialMetaIndex);

        // Update the citation data map, highlights, and meta index
        setCitationDataMap(updatedCitationDataMap);
        setCitationHighlights(mergedHighlights);
        setHighlightMetaIndex(updatedMetaIndex);
      } finally {
        // Always clear (even on the early `if (cancelled) return` inside), but
        // only for the live run — a superseded run must not flip the flag off
        // while the newer run is still building.
        if (!cancelled) setIsProcessingHighlights(false);
      }
    };

    processCitations();
    return () => { cancelled = true; };
  }, [documentAnswersData, pdfDocumentLoaded, searchText, documentId]);

  return {
    citationHighlights,
    citationDataMap,
    highlightMetaIndex,
    isProcessingHighlights,
  };
};
